import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';

import type { ParseKeys } from 'i18next';

import type { AppDispatch } from '/business/shared/state/store';

import type { QuestionConfigQuestionType } from '/index';
import type { ErrorType } from '/shared/components/types';
import type { ElectricityHeatSteamLocationPayload } from '/business/shared/state/selectors/application/electricityHeatSteam/electHeatSteamTypes';

import isNumeric from '/shared/functions/isNumeric';
import type { NumericInput } from '/shared/functions/isNumeric';
import countriesSelector from '/shared/state/selectors/countriesSelector';
import { urlApplicationRoot } from '/business/shared/util/Statics';
import { QUESTION_BRANCH_ACTIVITY } from '/shared/util/Statics';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import isApplicationAnswersEditableSelector from '/business/shared/state/selectors/application/workflow/isApplicationAnswersEditableSelector';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getElectLocationsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectLocationsSelector';
import getStaticPropsCountryRegionsSelector from '/business/shared/state/selectors/staticProps/getStaticPropsCountryRegionsSelector';
import getStaticPropsEgridsSelector from '/business/shared/state/selectors/staticProps/getStaticPropsEgridsSelector';
import saveLocation from '/business/shared/state/actions/basket/package2/saveLocation';
import useGetWasMetaSaveSuccessful from '/business/shared/components/questions/scope2/_hooks/useGetWasMetaSaveSuccessful';

import Button from '/shared/components/widgets/button/Button';
import LinkButton from '/shared/components/widgets/button/LinkButton';
import Input from '/shared/components/widgets/input/Input';
import Select from '/shared/components/widgets/select/Select';
import EducationWidget from '/business/shared/components/widgets/education/EducationWidget';
import ErrorsScroll from '/shared/components/functional/ErrorsScroll';
import CountrySelect from '/business/shared/components/widgets/application-meta/CountrySelect';

import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledRadio from '/shared/styled/components/StyledRadio';
import StyledFormLabel from '/shared/styled/form/StyledFormLabel';

import StyledElecFormRow from './components/__styled__/StyledElecFormRow';
import StyledElecFormButtons from './components/__styled__/StyledElecFormButtons';
import { StyledEgridGuidance, StyledEgridGuidanceText } from './components/__styled__/StyledEgridGuidance';

import {
  StyledMultiStepRadioGroup,
  StyledMultiStepSection,
  StyledMultiStepSummaryBody,
  StyledMultiStepSummaryButtons,
  StyledMultiStepSummaryCard,
  StyledMultiStepSummaryHeader,
  StyledMultiStepSummaryItem,
  StyledMultiStepSummaryTitle,
} from './components/__styled__/StyledMultiStepElements';

const REVIEW_STEP = 0;
const UNITED_STATES_COUNTRY_CODE = 'us';
const EGRID_MAPS_URL = 'https://www.epa.gov/egrid/maps';

const floorSpaceDropdownItems = [
  // { name: 'pleaseselect', displayName: 'Select a unit', isPlaceholder: true },
  { name: 'sqft', displayName: 'sq ft' },
  { name: 'm2', displayName: 'm²' },
];

const toNumberOrNull = (value: NumericInput) => (isNumeric(value) ? Number(value) : null);

export default function ElectricityHeatSteamLocationsAdd({ question }: { question: QuestionConfigQuestionType }) {
  const { t } = useTranslation();
  const appId = useSelector(getApplicationIdSelector);
  const { getUrlWithAppId } = useUrlBuilder();
  const locations = useSelector(getElectLocationsSelector);
  const countries = useSelector(countriesSelector);
  const countryRegions = useSelector(getStaticPropsCountryRegionsSelector) as {
    id: number;
    country_id: string;
    region_label: string;
  }[];
  const egrids = useSelector(getStaticPropsEgridsSelector) as {
    id: number;
    country_id: string;
    grid_label: string;
  }[];

  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const editId = params.get('edit');
  const isEditMode = Boolean(editId);
  const wasSaveSuccessful = useGetWasMetaSaveSuccessful();

  const currentLocation = isEditMode
    ? locations.find((existingLocation) => existingLocation.id?.toString() === editId)
    : undefined;

  const [formState, setFormState] = useState<Partial<ElectricityHeatSteamLocationPayload>>(
    currentLocation || {
      unique_name: '',
      country_code: '',
      address1: '',
      address2: '',
      town: '',
      region: null,
      grid: null,
      stateRegionCode: '',
      postcode: '',
      isWholeBuilding: null,
      isOwnMeter: null,
      sharedMeterTotalBuildingFloorSpace: null,
      sharedMeterTotalBuildingFloorSpaceUnit: null,
      sharedMeterBusinessOnlyFloorSpace: null,
      sharedMeterBusinessOnlyFloorSpaceUnit: null,
    },
  );
  const filteredRegions = countryRegions.filter((region) => region.country_id === formState.country_code);

  const filteredGrids = egrids
    .filter((grid) => {
      return grid.country_id === formState.country_code;
    })
    .sort((a, b) => a.grid_label.localeCompare(b.grid_label));

  const [fieldErrors, setFieldErrors] = useState<Record<string, ErrorType | false>>({});
  const [activeStep, setActiveStep] = useState(isEditMode ? REVIEW_STEP : 1);
  // if editing, we want to show all of the steps as already completed in review mode, so slam the highestStepVisited to something high
  // this is before steps are defined so we rely on it being clamped later
  const [highestStepVisited, setHighestStepVisited] = useState(isEditMode ? 7 : 1);
  const [returnToReviewAfterStep, setReturnToReviewAfterStep] = useState<number | null>(null);

  const isPartialBuilding = formState.isWholeBuilding === false;
  const shouldShowMeterStep = isPartialBuilding;
  const shouldShowFloorSpaceStep = shouldShowMeterStep && formState.isOwnMeter === false;
  const requiresFloorSpace = shouldShowFloorSpaceStep;
  const isUnitedStatesLocation = formState.country_code === UNITED_STATES_COUNTRY_CODE;
  const shouldShowRegionField = filteredRegions.length > 0;
  const shouldShowGridField = filteredGrids.length > 0;
  const shouldShowUnitedStatesRegionField = shouldShowRegionField && isUnitedStatesLocation;
  const shouldShowUnitedStatesGridField = shouldShowGridField && isUnitedStatesLocation;
  const shouldShowGenericRegionField = shouldShowRegionField && !shouldShowUnitedStatesRegionField;
  const shouldShowGenericGridField = shouldShowGridField && !shouldShowUnitedStatesGridField;
  const stepTitles: Record<number, string> = {
    1: t('questions:electricityHeatSteam.locationsAdd.steps.locationAddress'),
    2: t('questions:electricityHeatSteam.locationsAdd.steps.businessOccupies'),
    3: t('questions:electricityHeatSteam.locationsAdd.steps.energyConsumptionRecorded'),
    4: t('questions:electricityHeatSteam.locationsAdd.steps.businessLocationOccupancy'),
  };

  const visibleSteps = useMemo(() => {
    const steps: number[] = [1, 2];
    if (shouldShowMeterStep) {
      steps.push(3);
    }
    if (shouldShowFloorSpaceStep) {
      steps.push(4);
    }
    return steps;
  }, [shouldShowFloorSpaceStep, shouldShowMeterStep]);

  const lastVisibleStep = visibleSteps[visibleSteps.length - 1] ?? 1;
  const isReviewMode = activeStep === REVIEW_STEP;

  const isAfterActiveStep = (stepNumber: number) => activeStep !== REVIEW_STEP && stepNumber > activeStep;
  const shouldShowSummary = (stepNumber: number) =>
    isReviewMode || (activeStep !== REVIEW_STEP && stepNumber < activeStep && highestStepVisited >= stepNumber);

  const hasErrors = useMemo(() => Object.values(fieldErrors).some(Boolean), [fieldErrors]);
  const errors = useMemo(
    () => Array.from(Object.values(fieldErrors)).filter((error): error is ErrorType => Boolean(error)),
    [fieldErrors],
  );

  useEffect(() => {
    if (!requiresFloorSpace) {
      return;
    }

    setFormState((prev) => {
      let didChange = false;
      const nextState = { ...prev };
      if (!nextState.sharedMeterTotalBuildingFloorSpaceUnit) {
        nextState.sharedMeterTotalBuildingFloorSpaceUnit = 'm2';
        didChange = true;
      }

      if (!nextState.sharedMeterBusinessOnlyFloorSpaceUnit) {
        nextState.sharedMeterBusinessOnlyFloorSpaceUnit = 'm2';
        didChange = true;
      }

      return didChange ? nextState : prev;
    });
  }, [requiresFloorSpace]);

  useEffect(() => {
    if (requiresFloorSpace) {
      return;
    }

    setFormState((prev) => {
      const shouldUpdate =
        prev.sharedMeterTotalBuildingFloorSpace !== null ||
        prev.sharedMeterTotalBuildingFloorSpaceUnit !== null ||
        prev.sharedMeterBusinessOnlyFloorSpace !== null ||
        prev.sharedMeterBusinessOnlyFloorSpaceUnit !== null;

      if (!shouldUpdate) {
        return prev;
      }

      return {
        ...prev,
        sharedMeterTotalBuildingFloorSpace: null,
        sharedMeterTotalBuildingFloorSpaceUnit: null,
        sharedMeterBusinessOnlyFloorSpace: null,
        sharedMeterBusinessOnlyFloorSpaceUnit: null,
      };
    });

    setFieldErrors((prev) => {
      const updated = { ...prev };
      [
        'sharedMeterTotalBuildingFloorSpace',
        'sharedMeterTotalBuildingFloorSpaceUnit',
        'sharedMeterBusinessOnlyFloorSpace',
        'sharedMeterBusinessOnlyFloorSpaceUnit',
      ].forEach((field) => {
        if (updated[field]) {
          updated[field] = false;
        }
      });
      return updated;
    });
  }, [requiresFloorSpace]);

  useEffect(() => {
    if (shouldShowMeterStep) {
      return;
    }

    setFieldErrors((prev) => {
      if (!prev.isOwnMeter) {
        return prev;
      }

      return {
        ...prev,
        isOwnMeter: false,
      };
    });
  }, [shouldShowMeterStep]);

  useEffect(() => {
    if (activeStep === REVIEW_STEP) {
      return;
    }

    if (!visibleSteps.includes(activeStep)) {
      setActiveStep(lastVisibleStep);
    }
  }, [activeStep, lastVisibleStep, visibleSteps]);

  useEffect(() => {
    setHighestStepVisited((prev) => (prev > lastVisibleStep ? lastVisibleStep : prev));
  }, [lastVisibleStep]);

  useEffect(() => {
    if (isReviewMode && returnToReviewAfterStep !== null) {
      setReturnToReviewAfterStep(null);
    }
  }, [isReviewMode, returnToReviewAfterStep]);

  const updateFormField = <K extends keyof ElectricityHeatSteamLocationPayload>(
    field: K,
    value: ElectricityHeatSteamLocationPayload[K] | undefined,
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field as string]) {
      setFieldErrors((prev) => ({ ...prev, [field as string]: false }));
    }
    if (field === 'country_code') {
      setFormState((prev) => ({ ...prev, region: null, grid: null }));
    } else if (field === 'region') {
      setFormState((prev) => ({ ...prev, grid: null }));
    }
  };

  const buildValidationErrors = useCallback((): Record<string, ErrorType | false> => {
    const errorsMap: Record<string, ErrorType | false> = {};
    const validationError = (code: string, key: ParseKeys): ErrorType => ({
      code,
      message: t(key),
      isTranslated: true,
    });

    if (!formState.unique_name?.trim()) {
      errorsMap.unique_name = validationError(
        'ERRNOUNIQUELOCATIONNAME',
        'questions:electricityHeatSteam.locationsAdd.validation.uniqueName',
      );
    }
    if (!formState.country_code) {
      errorsMap.country_code = validationError(
        'ERRNOCOUNTRY',
        'questions:electricityHeatSteam.locationsAdd.validation.country',
      );
    }
    if (!formState.address1?.trim()) {
      errorsMap.address1 = validationError(
        'ERRNOADDRESS1',
        'questions:electricityHeatSteam.locationsAdd.validation.address',
      );
    }
    if (!formState.town?.trim()) {
      errorsMap.town = validationError('ERRNOTOWN', 'questions:electricityHeatSteam.locationsAdd.validation.town');
    }
    if (!formState.postcode?.trim()) {
      errorsMap.postcode = validationError(
        'ERRNOPOSTCODE',
        'questions:electricityHeatSteam.locationsAdd.validation.postcode',
      );
    }
    if (formState.isWholeBuilding === null) {
      errorsMap.isWholeBuilding = validationError(
        'ERRNOBUILDINGOCCUPANCY',
        'questions:electricityHeatSteam.locationsAdd.validation.option',
      );
    }
    if (formState.isOwnMeter === null && !formState.isWholeBuilding) {
      errorsMap.isOwnMeter = validationError(
        'ERRNOBUILDINGOCCUPANCY',
        'questions:electricityHeatSteam.locationsAdd.validation.option',
      );
    }
    if ((formState.region === 0 || !formState.region) && shouldShowRegionField) {
      errorsMap.region = validationError(
        'ERRNOREGION',
        'questions:electricityHeatSteam.locationsAdd.validation.region',
      );
    }
    if ((formState.grid === 0 || !formState.grid) && shouldShowGridField) {
      errorsMap.grid = validationError('ERRNOEGRID', 'questions:electricityHeatSteam.locationsAdd.validation.egrid');
    }

    const totalFloorSpace = toNumberOrNull(formState.sharedMeterTotalBuildingFloorSpace);
    const businessFloorSpace = toNumberOrNull(formState.sharedMeterBusinessOnlyFloorSpace);

    if (requiresFloorSpace) {
      if (!totalFloorSpace || totalFloorSpace <= 0) {
        errorsMap.sharedMeterTotalBuildingFloorSpace = validationError(
          'ERRNOFLOORSPACE',
          'questions:electricityHeatSteam.locationsAdd.validation.totalFloorSpace',
        );
      }
      if (!formState.sharedMeterTotalBuildingFloorSpaceUnit) {
        errorsMap.sharedMeterTotalBuildingFloorSpaceUnit = validationError(
          'ERRNOFLOORSPACEUNIT',
          'questions:electricityHeatSteam.locationsAdd.validation.unit',
        );
      }
      if (!businessFloorSpace || businessFloorSpace <= 0) {
        errorsMap.sharedMeterBusinessOnlyFloorSpace = validationError(
          'ERRNOFLOORSPACEBUSINESS',
          'questions:electricityHeatSteam.locationsAdd.validation.businessFloorSpace',
        );
      }
      if (businessFloorSpace && totalFloorSpace && totalFloorSpace < businessFloorSpace) {
        errorsMap.sharedMeterTotalBuildingFloorSpace = validationError(
          'ERRFLOORSPACEMISMATCH',
          'questions:electricityHeatSteam.locationsAdd.validation.floorSpaceMismatch',
        );
      }
      if (!formState.sharedMeterBusinessOnlyFloorSpaceUnit) {
        errorsMap.sharedMeterBusinessOnlyFloorSpaceUnit = validationError(
          'ERRNOFLOORSPACEBUSINESSUNIT',
          'questions:electricityHeatSteam.locationsAdd.validation.unit',
        );
      }
    }

    return errorsMap;
  }, [formState, requiresFloorSpace, shouldShowGridField, shouldShowRegionField, t]);

  const validateForm = useCallback((): boolean => {
    const errorsMap = buildValidationErrors();
    setFieldErrors(errorsMap);
    return Object.keys(errorsMap).length === 0;
  }, [buildValidationErrors]);

  const isStepVisible = useCallback((stepNumber: number) => visibleSteps.includes(stepNumber), [visibleSteps]);

  const getNextVisibleStep = useCallback(
    (stepNumber: number) => {
      if (!visibleSteps.length) {
        return REVIEW_STEP;
      }

      if (stepNumber === REVIEW_STEP) {
        return REVIEW_STEP;
      }

      const currentIndex = visibleSteps.indexOf(stepNumber);
      if (currentIndex === -1) {
        return visibleSteps[0];
      }

      return visibleSteps[currentIndex + 1] ?? REVIEW_STEP;
    },
    [visibleSteps],
  );

  const stepFieldGroups = useMemo(
    () => ({
      1: [
        'unique_name',
        'country_code',
        'address1',
        'town',
        'postcode',
        ...(shouldShowRegionField ? ['region'] : []),
        ...(shouldShowGridField ? ['grid'] : []),
      ],
      2: ['isWholeBuilding'],
      3: shouldShowMeterStep ? ['isOwnMeter'] : [],
      4: requiresFloorSpace
        ? [
            'sharedMeterTotalBuildingFloorSpace',
            'sharedMeterTotalBuildingFloorSpaceUnit',
            'sharedMeterBusinessOnlyFloorSpace',
            'sharedMeterBusinessOnlyFloorSpaceUnit',
          ]
        : [],
    }),
    [requiresFloorSpace, shouldShowGridField, shouldShowMeterStep, shouldShowRegionField],
  );

  const validateStep = useCallback(
    (stepNumber: number) => {
      const errorsMap = buildValidationErrors();
      const relevantFields = stepFieldGroups[stepNumber as keyof typeof stepFieldGroups] || [];

      if (!relevantFields.length) {
        return true;
      }

      setFieldErrors((prev) => {
        const updated = { ...prev };
        relevantFields.forEach((field) => {
          updated[field] = errorsMap[field] || false;
        });
        return updated;
      });
      return !relevantFields.some((field) => Boolean(errorsMap[field]));
    },
    [buildValidationErrors, stepFieldGroups],
  );

  const formStateToLocation = (): ElectricityHeatSteamLocationPayload => ({
    id: currentLocation?.id || undefined,
    unique_name: formState.unique_name?.trim() || '',
    aggregated_tonnage: toNumberOrNull(formState.aggregated_tonnage),
    aggregated_kwh: toNumberOrNull(formState.aggregated_kwh),
    country_code: formState.country_code || '',
    address1: formState.address1?.trim() || '',
    address2: formState.address2?.trim() || '',
    town: formState.town?.trim() || '',
    stateRegionCode: formState.stateRegionCode ?? '',
    region: formState.region !== 0 ? (formState.region ?? null) : null,
    grid: formState.grid !== 0 ? (formState.grid ?? null) : null,
    postcode: formState.postcode?.trim() || '',
    isWholeBuilding: formState.isWholeBuilding ?? true,
    isOwnMeter: formState.isOwnMeter ?? true,
    sharedMeterTotalBuildingFloorSpace: toNumberOrNull(formState.sharedMeterTotalBuildingFloorSpace),
    // set to unit fallback to m2, to match the api default. So the diff-check doesn't think we've changed it when we haven't
    sharedMeterTotalBuildingFloorSpaceUnit: formState.sharedMeterTotalBuildingFloorSpaceUnit ?? 'm2',
    sharedMeterBusinessOnlyFloorSpace: toNumberOrNull(formState.sharedMeterBusinessOnlyFloorSpace),
    sharedMeterBusinessOnlyFloorSpaceUnit: formState.sharedMeterBusinessOnlyFloorSpaceUnit ?? 'm2',
  });

  const listRoute = getUrlWithAppId(
    `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam/locations`,
    appId,
  );
  const selectedRegionLabel = filteredRegions.find((region) => region.id === formState.region)?.region_label;
  const selectedGridLabel = filteredGrids.find((grid) => grid.id === formState.grid)?.grid_label;

  const handleNext = () => {
    if (activeStep === REVIEW_STEP) {
      return;
    }

    if (!validateStep(activeStep)) {
      return;
    }

    if (
      returnToReviewAfterStep !== null &&
      returnToReviewAfterStep === activeStep &&
      highestStepVisited >= lastVisibleStep
    ) {
      setHighestStepVisited(lastVisibleStep);
      setReturnToReviewAfterStep(null);
      setActiveStep(REVIEW_STEP);
      return;
    }

    const nextStep = getNextVisibleStep(activeStep);

    if (nextStep === REVIEW_STEP) {
      setHighestStepVisited(lastVisibleStep);
      setReturnToReviewAfterStep(null);
      setActiveStep(REVIEW_STEP);
      return;
    }

    setActiveStep(nextStep);
    setHighestStepVisited((prev) => Math.max(prev, nextStep));

    if (returnToReviewAfterStep === activeStep) {
      setReturnToReviewAfterStep(null);
    }
  };

  const handleEditClick = (stepNumber: number) => {
    if (isStepVisible(stepNumber) && stepNumber <= highestStepVisited) {
      if (isReviewMode) {
        setReturnToReviewAfterStep(stepNumber);
      }
      setActiveStep(stepNumber);
    }
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isReviewMode) {
      handleNext();
      return;
    }

    if (!validateForm()) {
      return;
    }

    const locationPayload = formStateToLocation();

    await dispatch(
      saveLocation({
        location: locationPayload,
        applicationId: appId,
      }),
    );
  };
  const isEditable = useSelector(isApplicationAnswersEditableSelector);

  useEffect(() => {
    if (wasSaveSuccessful) {
      navigate(listRoute);
    }
  }, [wasSaveSuccessful, listRoute, navigate]);

  if (isEditMode && !currentLocation) {
    return <div>{t('questions:electricityHeatSteam.locationsAdd.locationNotFound')}</div>;
  }

  const primaryButtonLabel = isReviewMode
    ? t(isEditMode ? 'business:Buttons.update' : 'business:Buttons.save')
    : t('questions:electricityHeatSteam.shared.next');

  return (
    <>
      <EducationWidget question={question} branchType={QUESTION_BRANCH_ACTIVITY} />

      <div>
        <StyledHeading as="h1" $type="h1">
          {isEditMode ? (
            <>
              {/* @i18n: Electricity, heat and steam - Edit location */}
              <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.editTitle" />
            </>
          ) : (
            <>
              {/* @i18n: Electricity, heat and steam - Add a new location */}
              <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.addTitle" />
            </>
          )}
        </StyledHeading>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          {/* @i18n: For each location, you can choose whether your business operates across the whole[...] */}
          <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.intro" />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>
            {/* @i18n: Refer to the "Help" section for detailed guidance. */}
            <Trans i18nKey="questions:electricityHeatSteam.shared.referToHelpText" />
          </div>
        </StyledInfoFlash>
        {hasErrors && <ErrorsScroll errors={errors} hasErrors={hasErrors} />}
      </div>
      <div>
        {activeStep === 0 && (
          <StyledHeading as="h2" $type="h2">
            {/* @i18n: Summary of location details */}
            <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.summaryTitle" />
          </StyledHeading>
        )}
        {activeStep === 1 && (
          <StyledHeading as="h2" $type="h2">
            {/* @i18n: Location details */}
            <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.locationDetailsTitle" />
          </StyledHeading>
        )}
        <form onSubmit={handleSave}>
          {!isAfterActiveStep(1) &&
            (activeStep === 1 ? (
              <StyledMultiStepSection>
                <StyledHeading as="h3" $type="h3">
                  {/* @i18n: Location identifier */}
                  <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.locationIdentifier" />
                </StyledHeading>
                <StyledElecFormRow>
                  <label htmlFor="uniqueName">
                    {t('questions:electricityHeatSteam.locationsAdd.labels.locationName')}
                  </label>
                  <Input
                    id="uniqueName"
                    type="text"
                    $isErrored={Boolean(fieldErrors.unique_name)}
                    placeholder={t('questions:electricityHeatSteam.locationsAdd.placeholders.locationName')}
                    value={formState.unique_name || ''}
                    onChange={(event) => updateFormField('unique_name', event.target.value)}
                  />
                </StyledElecFormRow>

                <StyledHeading as="h3" $type="h3">
                  {/* @i18n: Select a country */}
                  <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.selectCountryTitle" />
                </StyledHeading>
                <StyledElecFormRow>
                  <label htmlFor="country">{t('questions:electricityHeatSteam.locationsAdd.labels.country')}</label>
                  <CountrySelect
                    isLocationForm
                    countryVal={formState.country_code || ''}
                    setCountryVal={(value) => updateFormField('country_code', value)}
                    context={`electricityHeatSteamLocation-${currentLocation?.id || 'new'}`}
                  />
                </StyledElecFormRow>

                <StyledHeading as="h3" $type="h3">
                  {/* @i18n: Location address */}
                  <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.steps.locationAddress" />
                </StyledHeading>
                <StyledElecFormRow>
                  <label htmlFor="address1">
                    {t('questions:electricityHeatSteam.locationsAdd.labels.addressLine1')}
                  </label>
                  <Input
                    id="address1"
                    type="text"
                    $isErrored={Boolean(fieldErrors.address1)}
                    placeholder={t('questions:electricityHeatSteam.locationsAdd.placeholders.address')}
                    value={formState.address1 || ''}
                    onChange={(event) => updateFormField('address1', event.target.value)}
                  />
                </StyledElecFormRow>

                <StyledElecFormRow>
                  <label htmlFor="address2">
                    {t('questions:electricityHeatSteam.locationsAdd.labels.addressLine2')}
                  </label>
                  <Input
                    id="address2"
                    type="text"
                    placeholder={t('questions:electricityHeatSteam.locationsAdd.placeholders.address')}
                    value={formState.address2 || ''}
                    onChange={(event) => updateFormField('address2', event.target.value)}
                  />
                </StyledElecFormRow>

                <StyledElecFormRow>
                  <label htmlFor="town">
                    {t(
                      isUnitedStatesLocation
                        ? 'questions:electricityHeatSteam.locationsAdd.labels.townCityUs'
                        : 'questions:electricityHeatSteam.locationsAdd.labels.townCity',
                    )}
                  </label>
                  <Input
                    id="town"
                    type="text"
                    $isErrored={Boolean(fieldErrors.town)}
                    placeholder={t('questions:electricityHeatSteam.locationsAdd.placeholders.town')}
                    value={formState.town || ''}
                    onChange={(event) => updateFormField('town', event.target.value)}
                  />
                </StyledElecFormRow>

                {shouldShowUnitedStatesRegionField && (
                  <StyledElecFormRow>
                    <label htmlFor="region">
                      {t('questions:electricityHeatSteam.locationsAdd.labels.stateRegion')}
                    </label>
                    <Select
                      id="region"
                      value={formState.region ?? ''}
                      onChange={(event) => updateFormField('region', parseInt(event.target.value, 10))}
                    >
                      <option key={0} value={0}>
                        {t('questions:electricityHeatSteam.locationsAdd.placeholders.stateRegion')}
                      </option>
                      {filteredRegions.map((region) => (
                        <option key={region.id} value={region.id}>
                          {region.region_label}
                        </option>
                      ))}
                    </Select>
                  </StyledElecFormRow>
                )}

                <StyledElecFormRow>
                  <label htmlFor="postcode">
                    {t(
                      isUnitedStatesLocation
                        ? 'questions:electricityHeatSteam.locationsAdd.labels.zipPostalCodeUs'
                        : 'questions:electricityHeatSteam.locationsAdd.labels.zipPostalCode',
                    )}
                  </label>
                  <Input
                    id="postcode"
                    type="text"
                    $isErrored={Boolean(fieldErrors.postcode)}
                    placeholder={t('questions:electricityHeatSteam.locationsAdd.placeholders.postalCode')}
                    value={formState.postcode || ''}
                    onChange={(event) => updateFormField('postcode', event.target.value)}
                  />
                </StyledElecFormRow>
                {shouldShowGenericRegionField && (
                  <>
                    <StyledHeading as="h3" $type="h3">
                      {/* @i18n: Select a region */}
                      <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.selectRegionTitle" />
                    </StyledHeading>
                    <StyledElecFormRow>
                      <label htmlFor="region">{t('questions:electricityHeatSteam.locationsAdd.labels.region')}</label>
                      <Select
                        id="region"
                        value={formState.region ?? ''}
                        onChange={(event) => updateFormField('region', parseInt(event.target.value, 10))}
                      >
                        <option key={0} value={0}>
                          {t('questions:electricityHeatSteam.locationsAdd.placeholders.region')}
                        </option>
                        {filteredRegions.map((region) => (
                          <option key={region.id} value={region.id}>
                            {region.region_label}
                          </option>
                        ))}
                      </Select>
                    </StyledElecFormRow>
                  </>
                )}

                {shouldShowUnitedStatesGridField && (
                  <>
                    <StyledHeading as="h3" $type="h3">
                      {/* @i18n: Choose your eGRID subregion */}
                      <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.chooseEgridTitle" />
                    </StyledHeading>
                    <StyledElecFormRow>
                      <label htmlFor="grid">{t('questions:electricityHeatSteam.locationsAdd.labels.egrid')}</label>
                      <Select
                        id="grid"
                        disabled={filteredGrids.length === 0}
                        value={formState.grid ?? ''}
                        onChange={(event) => updateFormField('grid', parseInt(event.target.value, 10))}
                      >
                        <option key={0} value={0}>
                          {t('questions:electricityHeatSteam.locationsAdd.placeholders.egrid')}
                        </option>
                        {filteredGrids.map((grid) => (
                          <option key={grid.id} value={grid.id}>
                            {grid.grid_label}
                          </option>
                        ))}
                      </Select>
                    </StyledElecFormRow>
                    <StyledEgridGuidance>
                      <StyledParagraph $largeText $styleLinks>
                        <a href={EGRID_MAPS_URL} target="_blank" rel="noopener noreferrer">
                          {/* @i18n: Find out more about eGRID subregions */}
                          <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.egridLink" />
                        </a>
                      </StyledParagraph>
                      <StyledEgridGuidanceText $largeText>
                        {/* @i18n: The EPA’s eGRID subregion map is a comprehensive source of data on the environmental[...] */}
                        <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.egridBody1" />
                      </StyledEgridGuidanceText>
                      <StyledEgridGuidanceText $largeText>
                        {/* @i18n: Click on the area of the map where your location is located to bring up that[...] */}
                        <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.egridBody2" />
                      </StyledEgridGuidanceText>
                    </StyledEgridGuidance>
                  </>
                )}

                {shouldShowGenericGridField && (
                  <>
                    <StyledHeading as="h3" $type="h3">
                      {/* @i18n: Select a grid */}
                      <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.selectGridTitle" />
                    </StyledHeading>
                    <StyledElecFormRow>
                      <label htmlFor="grid">{t('questions:electricityHeatSteam.locationsAdd.labels.grid')}</label>
                      <Select
                        id="grid"
                        disabled={filteredGrids.length === 0}
                        value={formState.grid ?? ''}
                        onChange={(event) => updateFormField('grid', parseInt(event.target.value, 10))}
                      >
                        <option key={0} value={0}>
                          {t('questions:electricityHeatSteam.locationsAdd.placeholders.grid')}
                        </option>
                        {filteredGrids.map((grid) => (
                          <option key={grid.id} value={grid.id}>
                            {grid.grid_label}
                          </option>
                        ))}
                      </Select>
                    </StyledElecFormRow>
                  </>
                )}
              </StyledMultiStepSection>
            ) : (
              shouldShowSummary(1) && (
                <StyledMultiStepSummaryCard>
                  <StyledMultiStepSummaryHeader>
                    <StyledMultiStepSummaryTitle>
                      <StyledHeading as="h3" $type="h3" $semibold $noMargin>
                        {stepTitles[1]}
                      </StyledHeading>
                    </StyledMultiStepSummaryTitle>
                    <StyledMultiStepSummaryButtons>
                      <Button type="button" $secondary $small onClick={() => handleEditClick(1)}>
                        {t('questions:electricityHeatSteam.shared.edit')}
                      </Button>
                    </StyledMultiStepSummaryButtons>
                  </StyledMultiStepSummaryHeader>
                  <StyledMultiStepSummaryBody>
                    <div>
                      <span>{formState.unique_name || t('questions:electricityHeatSteam.shared.notProvided')}</span>
                    </div>
                    <div>
                      <div>
                        {[
                          formState.address1,
                          formState.address2,
                          formState.town,
                          formState.stateRegionCode || selectedRegionLabel,
                          formState.postcode,
                          selectedGridLabel,
                        ]
                          .filter(Boolean)
                          .map((line) => (
                            <div key={line}>{line}</div>
                          ))}
                        <div>
                          {countries?.find(({ key }) => key === formState.country_code)?.value ||
                            t('questions:electricityHeatSteam.shared.notProvided')}
                        </div>
                      </div>
                    </div>
                  </StyledMultiStepSummaryBody>
                </StyledMultiStepSummaryCard>
              )
            ))}

          {!isAfterActiveStep(2) &&
            (activeStep === 2 || highestStepVisited >= 2) &&
            (activeStep === 2 ? (
              <StyledMultiStepSection>
                <StyledHeading>
                  {/* @i18n: Does your business occupy the whole location or do you share occupancy with multiple[...] */}
                  <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.occupancyQuestion" />
                </StyledHeading>
                <StyledMultiStepRadioGroup
                  role="radiogroup"
                  aria-label={t('questions:electricityHeatSteam.locationsAdd.ariaLabels.buildingOccupancy')}
                >
                  <StyledFormLabel $isRow $noWrap $noBottomPadding>
                    <StyledRadio
                      name="building-occupancy"
                      checked={formState.isWholeBuilding === true}
                      $isErrored={
                        fieldErrors.isWholeBuilding && fieldErrors.isWholeBuilding.code === 'ERRNOBUILDINGOCCUPANCY'
                      }
                      onChange={() => updateFormField('isWholeBuilding', true)}
                    />
                    <span>{t('questions:electricityHeatSteam.locationsAdd.options.wholeLocation')}</span>
                  </StyledFormLabel>
                  <StyledFormLabel $isRow $noWrap $noBottomPadding>
                    <StyledRadio
                      name="building-occupancy"
                      checked={formState.isWholeBuilding === false}
                      $isErrored={
                        fieldErrors.isWholeBuilding && fieldErrors.isWholeBuilding.code === 'ERRNOBUILDINGOCCUPANCY'
                      }
                      onChange={() => updateFormField('isWholeBuilding', false)}
                    />
                    <span>{t('questions:electricityHeatSteam.locationsAdd.options.partOfLocation')}</span>
                  </StyledFormLabel>
                </StyledMultiStepRadioGroup>
              </StyledMultiStepSection>
            ) : (
              shouldShowSummary(2) && (
                <StyledMultiStepSummaryCard>
                  <StyledMultiStepSummaryHeader>
                    <StyledMultiStepSummaryTitle>
                      <StyledHeading as="h3" $type="h3" $semibold $noMargin>
                        {stepTitles[2]}
                      </StyledHeading>
                    </StyledMultiStepSummaryTitle>
                    <StyledMultiStepSummaryButtons>
                      <Button type="button" $secondary $small onClick={() => handleEditClick(2)}>
                        {t('questions:electricityHeatSteam.shared.edit')}
                      </Button>
                    </StyledMultiStepSummaryButtons>
                  </StyledMultiStepSummaryHeader>
                  <StyledMultiStepSummaryBody>
                    {t(
                      formState.isWholeBuilding
                        ? 'questions:electricityHeatSteam.locationsAdd.summary.wholeLocation'
                        : 'questions:electricityHeatSteam.locationsAdd.summary.partOfLocation',
                    )}
                  </StyledMultiStepSummaryBody>
                </StyledMultiStepSummaryCard>
              )
            ))}

          {shouldShowMeterStep &&
            !isAfterActiveStep(3) &&
            (activeStep === 3 || highestStepVisited >= 3) &&
            (activeStep === 3 ? (
              <StyledMultiStepSection>
                <StyledHeading>
                  {/* @i18n: Does your business have access to its own meter or is there a meter shared across the[...] */}
                  <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.meterQuestion" />
                </StyledHeading>
                <StyledMultiStepRadioGroup
                  role="radiogroup"
                  aria-label={t('questions:electricityHeatSteam.locationsAdd.ariaLabels.meterAccess')}
                >
                  <StyledFormLabel $isRow $noWrap $noBottomPadding>
                    <StyledRadio
                      name="meter-access"
                      $isErrored={fieldErrors.isOwnMeter && fieldErrors.isOwnMeter.code === 'ERRNOBUILDINGOCCUPANCY'}
                      checked={formState.isOwnMeter === true}
                      onChange={() => updateFormField('isOwnMeter', true)}
                    />
                    <span>{t('questions:electricityHeatSteam.locationsAdd.options.ownMeter')}</span>
                  </StyledFormLabel>
                  <StyledFormLabel $isRow $noWrap $noBottomPadding>
                    <StyledRadio
                      name="meter-access"
                      $isErrored={fieldErrors.isOwnMeter && fieldErrors.isOwnMeter.code === 'ERRNOBUILDINGOCCUPANCY'}
                      checked={formState.isOwnMeter === false}
                      onChange={() => updateFormField('isOwnMeter', false)}
                    />
                    <span>{t('questions:electricityHeatSteam.locationsAdd.options.sharedMeter')}</span>
                  </StyledFormLabel>
                </StyledMultiStepRadioGroup>
              </StyledMultiStepSection>
            ) : (
              shouldShowSummary(3) && (
                <StyledMultiStepSummaryCard>
                  <StyledMultiStepSummaryHeader>
                    <StyledMultiStepSummaryTitle>
                      <StyledHeading as="h3" $type="h3" $semibold $noMargin>
                        {stepTitles[3]}
                      </StyledHeading>
                    </StyledMultiStepSummaryTitle>
                    <StyledMultiStepSummaryButtons>
                      <Button type="button" $secondary $small onClick={() => handleEditClick(3)}>
                        {t('questions:electricityHeatSteam.shared.edit')}
                      </Button>
                    </StyledMultiStepSummaryButtons>
                  </StyledMultiStepSummaryHeader>
                  <StyledMultiStepSummaryBody>
                    {t(
                      formState.isOwnMeter
                        ? 'questions:electricityHeatSteam.locationsAdd.summary.ownMeter'
                        : 'questions:electricityHeatSteam.locationsAdd.summary.sharedMeter',
                    )}
                  </StyledMultiStepSummaryBody>
                </StyledMultiStepSummaryCard>
              )
            ))}

          {shouldShowFloorSpaceStep &&
            !isAfterActiveStep(4) &&
            (activeStep === 4 || highestStepVisited >= 4) &&
            (activeStep === 4 ? (
              <StyledMultiStepSection>
                <>
                  <StyledHeading as="h3" $type="h3">
                    {/* @i18n: Provide floor space details */}
                    <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.floorSpaceTitle" />
                  </StyledHeading>
                  <StyledElecFormRow>
                    <label htmlFor="totalFloorSpace">
                      {t('questions:electricityHeatSteam.locationsAdd.labels.totalFloorSpace')}
                    </label>
                    <Input
                      id="totalFloorSpace"
                      inputMode="decimal"
                      step="0.000001"
                      placeholder="0.00"
                      value={formState.sharedMeterTotalBuildingFloorSpace ?? ''}
                      onChange={(event) => updateFormField('sharedMeterTotalBuildingFloorSpace', event.target.value)}
                      $isErrored={Boolean(
                        fieldErrors.sharedMeterTotalBuildingFloorSpace ||
                          fieldErrors.sharedMeterTotalBuildingFloorSpaceUnit,
                      )}
                      dropdownItems={floorSpaceDropdownItems}
                      dropdownValue={formState.sharedMeterTotalBuildingFloorSpaceUnit || 'm2'}
                      onDropdownChange={(event) => {
                        updateFormField(
                          'sharedMeterTotalBuildingFloorSpaceUnit',
                          event.target.value ? (event.target.value as 'sqft' | 'm2') : null,
                        );
                        updateFormField(
                          'sharedMeterBusinessOnlyFloorSpaceUnit',
                          event.target.value ? (event.target.value as 'sqft' | 'm2') : null,
                        );
                      }}
                    />
                  </StyledElecFormRow>

                  <StyledElecFormRow>
                    <label htmlFor="businessFloorSpace">
                      {t('questions:electricityHeatSteam.locationsAdd.labels.businessFloorSpace')}
                    </label>
                    <Input
                      id="businessFloorSpace"
                      inputMode="decimal"
                      step="0.000001"
                      placeholder="0.00"
                      value={formState.sharedMeterBusinessOnlyFloorSpace ?? ''}
                      onChange={(event) => updateFormField('sharedMeterBusinessOnlyFloorSpace', event.target.value)}
                      $isErrored={Boolean(
                        fieldErrors.sharedMeterBusinessOnlyFloorSpace ||
                          fieldErrors.sharedMeterBusinessOnlyFloorSpaceUnit,
                      )}
                      dropdownItems={floorSpaceDropdownItems}
                      dropdownValue={formState.sharedMeterBusinessOnlyFloorSpaceUnit || 'm2'}
                      onDropdownChange={(event) => {
                        updateFormField(
                          'sharedMeterTotalBuildingFloorSpaceUnit',
                          event.target.value ? (event.target.value as 'sqft' | 'm2') : null,
                        );
                        updateFormField(
                          'sharedMeterBusinessOnlyFloorSpaceUnit',
                          event.target.value ? (event.target.value as 'sqft' | 'm2') : null,
                        );
                      }}
                    />
                  </StyledElecFormRow>
                </>
              </StyledMultiStepSection>
            ) : (
              shouldShowSummary(4) && (
                <StyledMultiStepSummaryCard>
                  <StyledMultiStepSummaryHeader>
                    <StyledMultiStepSummaryTitle>
                      <StyledHeading as="h3" $type="h3" $semibold $noMargin>
                        {stepTitles[4]}
                      </StyledHeading>
                    </StyledMultiStepSummaryTitle>
                    <StyledMultiStepSummaryButtons>
                      <Button type="button" $secondary $small onClick={() => handleEditClick(4)}>
                        {t('questions:electricityHeatSteam.shared.edit')}
                      </Button>
                    </StyledMultiStepSummaryButtons>
                  </StyledMultiStepSummaryHeader>
                  <StyledMultiStepSummaryBody>
                    <>
                      <StyledMultiStepSummaryItem>
                        <div>{t('questions:electricityHeatSteam.locationsAdd.labels.totalFloorSpace')}</div>
                        <div>
                          {formState.sharedMeterTotalBuildingFloorSpace
                            ? `${formState.sharedMeterTotalBuildingFloorSpace} ${t(`business:emissionFactors.areaUnitsSm.${formState.sharedMeterTotalBuildingFloorSpaceUnit}`, 'm²')}`
                            : t('questions:electricityHeatSteam.shared.notProvided')}
                        </div>
                      </StyledMultiStepSummaryItem>
                      <StyledMultiStepSummaryItem>
                        <div>{t('questions:electricityHeatSteam.locationsAdd.labels.businessFloorSpace')}</div>
                        <div>
                          {formState.sharedMeterBusinessOnlyFloorSpace
                            ? `${formState.sharedMeterBusinessOnlyFloorSpace} ${t(`business:emissionFactors.areaUnitsSm.${formState.sharedMeterBusinessOnlyFloorSpaceUnit}`, 'm²')}`
                            : t('questions:electricityHeatSteam.shared.notProvided')}
                        </div>
                      </StyledMultiStepSummaryItem>
                    </>
                  </StyledMultiStepSummaryBody>
                </StyledMultiStepSummaryCard>
              )
            ))}

          <StyledElecFormButtons>
            <Button $primary disabled={!isEditable} type="submit">
              {primaryButtonLabel}
            </Button>
            <LinkButton $secondary to={listRoute}>
              {/* @i18n: Cancel and return to Locations */}
              <Trans i18nKey="questions:electricityHeatSteam.locationsAdd.cancelReturn" />
            </LinkButton>
          </StyledElecFormButtons>
        </form>
      </div>
    </>
  );
}
